import React, { createContext, useContext, useEffect, useRef } from 'react';
import {
	Collapse as AntdCollapse,
	CollapseProps as AntdCollapseProps,
	CollapsePanelProps as AntdCollapsePanelProps
} from 'antd';
import classNames from 'classnames';

import './collapse.scss';

interface CollapseProps extends AntdCollapseProps {
	onChange?: (keys) => void;
	testId?: string;
	isBringgCollapse?: boolean;
	wrapClassName?: string;
	removePadding?: boolean;
	removeHeaderPadding?: boolean;
	removeContentPadding?: boolean;
}

export const DEFAULT_PANEL_KEY = 'bringg-collapse-panel';

const CollapseContext = createContext<{ wrapperRef: React.MutableRefObject<HTMLDivElement> } | null>(null);

const Collapse = (props: CollapseProps) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const {
		isBringgCollapse = true,
		testId,
		wrapClassName,
		className,
		removePadding,
		removeHeaderPadding,
		removeContentPadding
	} = props;

	const children = [];

	// we use of forEach instead of map because React.Children.map add prefix to the original key
	React.Children.forEach(props.children as React.ReactElement<CollapsePanelProps>, (child, index) => {
		if (child) {
			children.push(React.cloneElement(child, { _index: index }));
		}
	});

	return (
		<CollapseContext.Provider key={testId} value={{ wrapperRef }}>
			<div
				ref={wrapperRef}
				className={classNames(
					{
						'bringg-collapse': isBringgCollapse,
						'remove-collapse-header-padding':
							removeHeaderPadding != null ? removeHeaderPadding : removePadding,
						'remove-collapse-content-padding':
							removeContentPadding != null ? removeContentPadding : removePadding
					},
					wrapClassName,
					className
				)}
				data-test-id={testId}
			>
				<AntdCollapse bordered={false} {...props} children={children} />
			</div>
		</CollapseContext.Provider>
	);
};

interface CollapsePanelProps extends AntdCollapsePanelProps {
	_index?: number;
	headerClassName?: string;
}

const CollapsePanel = ({ headerClassName, _index, ...props }: CollapsePanelProps) => {
	const wrapperContext = useContext(CollapseContext);

	// an option to add custom className
	// saves us to override antd css
	useEffect(() => {
		if (headerClassName) {
			const headerElement = wrapperContext.wrapperRef?.current.querySelectorAll('.ant-collapse-header')[_index];
			headerElement?.classList.add(headerClassName);
		}
	}, [headerClassName, wrapperContext.wrapperRef, _index]);

	return <AntdCollapse.Panel {...props} />;
};

Collapse.Panel = CollapsePanel;

export default Collapse;
